// SMS segment counting (GSM-7 vs UCS-2).
//
// Carriers bill per segment, not per message, so the editor must show what a
// message will actually cost. A message that fits the GSM-7 alphabet packs
// 160 chars into one segment (153 per segment once it splits, the rest is
// header). Any character outside GSM-7 forces the whole message to UCS-2:
// 70 chars per segment, 67 when split.
//
// Frontend preview only. The authoritative segment count comes from Twilio's
// send response and is stored on sms_events.num_segments for billing.

const GSM7_BASIC =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?" +
  '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';

// These cost two GSM-7 characters each (escape + char).
const GSM7_EXTENDED = '^{}\\[~]|€';

export type SmsEncoding = 'GSM-7' | 'UCS-2';

export interface SegmentInfo {
  encoding: SmsEncoding;
  /** Billable character units (GSM-7 extended chars count as 2). */
  length: number;
  segments: number;
  /** Characters still available before another segment is added. */
  remaining: number;
  perSegment: number;
}

function isGsm7(text: string): boolean {
  for (const char of text) {
    if (GSM7_BASIC.indexOf(char) === -1 && GSM7_EXTENDED.indexOf(char) === -1) {
      return false;
    }
  }
  return true;
}

function gsm7Length(text: string): number {
  let length = 0;
  for (const char of text) {
    length += GSM7_EXTENDED.indexOf(char) !== -1 ? 2 : 1;
  }
  return length;
}

export function calculateSegments(text: string): SegmentInfo {
  const value = text || '';

  if (value.length === 0) {
    return { encoding: 'GSM-7', length: 0, segments: 0, remaining: 160, perSegment: 160 };
  }

  const gsm = isGsm7(value);
  const encoding: SmsEncoding = gsm ? 'GSM-7' : 'UCS-2';

  // Counts UTF-16 units for UCS-2, matching Twilio and the otc-sms layer's
  // segments.js: an emoji (surrogate pair) is two units.
  const length = gsm ? gsm7Length(value) : value.length;

  const singleMax = gsm ? 160 : 70;
  const multiMax = gsm ? 153 : 67;

  if (length <= singleMax) {
    return { encoding, length, segments: 1, remaining: singleMax - length, perSegment: singleMax };
  }

  const segments = Math.ceil(length / multiMax);
  return {
    encoding,
    length,
    segments,
    remaining: segments * multiMax - length,
    perSegment: multiMax,
  };
}
